import { fetchAuthSession } from 'aws-amplify/auth';
import outputs from '../../amplify_outputs.json';

const GRAPHQL_ENDPOINT = outputs.data.url;
const REQUEST_TIMEOUT_MS = 10_000;
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1_000;

export interface ShiftSummaryRecord {
  id: string;
  date: string;
  shift: 'NIGHT' | 'DAY' | 'EVENING';
  shiftStart: string;
  shiftEnd?: string | null;
  onDutyTech: string;
  onDutyTechId: string;
  submittedBy?: string | null;
  lastUpdatedBy?: string | null;
  contributors?: string | null;
  submittedAt?: string | null;
  acknowledgedBy?: string | null;
  acknowledgedAt?: string | null;
  notes?: string | null;
  urgentTicketsJson?: string | null;
  currentTicketsJson?: string | null;
  maintenanceItemsJson?: string | null;
  owner?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

const SUMMARY_FIELDS = `
  id date shift shiftStart shiftEnd onDutyTech onDutyTechId
  submittedBy lastUpdatedBy contributors
  submittedAt acknowledgedBy notes urgentTicketsJson currentTicketsJson maintenanceItemsJson
`;

function isRetryable(error: unknown, status?: number): boolean {
  if (status && status >= 500) return true;
  if (status === 429) return true;
  if (error instanceof TypeError) return true; // network error
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  return false;
}

async function graphqlRequest(query: string, variables?: Record<string, unknown>) {
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
      await new Promise((r) => setTimeout(r, delay));
    }

    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error('No auth token available');

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ query, variables }),
        signal: controller.signal,
      });
    } catch (err) {
      clearTimeout(timeout);
      lastError = err;
      if (isRetryable(err)) {
        console.warn(`[shiftApi] Request failed (attempt ${attempt + 1}/${MAX_RETRIES}):`, err);
        continue;
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }

    if (!response.ok && isRetryable(null, response.status)) {
      console.warn(`[shiftApi] HTTP ${response.status} (attempt ${attempt + 1}/${MAX_RETRIES})`);
      lastError = new Error(`HTTP ${response.status}`);
      continue;
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    let result;
    try {
      result = await response.json();
    } catch {
      throw new Error('Failed to parse API response');
    }

    if (result.errors && !result.data) {
      console.error('[shiftApi] GraphQL errors:', result.errors);
      throw new Error(result.errors.map((e: { message: string }) => e.message).join(', '));
    }
    if (result.errors) {
      console.warn('[shiftApi] GraphQL partial errors:', result.errors.length, 'warnings');
    }
    return result.data;
  }

  throw lastError ?? new Error('Request failed after retries');
}

const CREATE_SHIFT_SUMMARY = `
  mutation CreateShiftSummary($input: CreateShiftSummaryInput!) {
    createShiftSummary(input: $input) {
      ${SUMMARY_FIELDS}
    }
  }
`;

const UPDATE_SHIFT_SUMMARY = `
  mutation UpdateShiftSummary($input: UpdateShiftSummaryInput!) {
    updateShiftSummary(input: $input) {
      ${SUMMARY_FIELDS}
    }
  }
`;

const DELETE_SHIFT_SUMMARY = `
  mutation DeleteShiftSummary($input: DeleteShiftSummaryInput!) {
    deleteShiftSummary(input: $input) {
      id
    }
  }
`;

const LIST_SHIFT_SUMMARIES = `
  query ListShiftSummaries($limit: Int, $nextToken: String) {
    listShiftSummaries(limit: $limit, nextToken: $nextToken) {
      items {
        ${SUMMARY_FIELDS}
      }
      nextToken
    }
  }
`;

const GET_SHIFT_SUMMARY = `
  query GetShiftSummary($id: ID!) {
    getShiftSummary(id: $id) {
      ${SUMMARY_FIELDS}
      updatedAt
    }
  }
`;

const LIST_BY_DATE_AND_SHIFT = `
  query ListByDateAndShift($date: AWSDate!, $shift: ModelStringKeyConditionInput) {
    listShiftSummaryByDateAndShift(date: $date, shift: $shift) {
      items {
        ${SUMMARY_FIELDS}
      }
    }
  }
`;

export async function createShiftSummary(
  input: Omit<ShiftSummaryRecord, 'createdAt' | 'updatedAt'>,
): Promise<ShiftSummaryRecord> {
  const data = await graphqlRequest(CREATE_SHIFT_SUMMARY, { input });
  return data.createShiftSummary;
}

export async function updateShiftSummary(
  input: Partial<ShiftSummaryRecord> & { id: string },
): Promise<ShiftSummaryRecord> {
  const data = await graphqlRequest(UPDATE_SHIFT_SUMMARY, { input });
  return data.updateShiftSummary;
}

export async function deleteShiftSummary(id: string): Promise<void> {
  await graphqlRequest(DELETE_SHIFT_SUMMARY, { input: { id } });
}

export async function getShiftSummaryById(
  id: string,
): Promise<ShiftSummaryRecord | null> {
  try {
    const data = await graphqlRequest(GET_SHIFT_SUMMARY, { id });
    return (data.getShiftSummary as ShiftSummaryRecord | null) ?? null;
  } catch (err) {
    console.warn('[shiftApi] getShiftSummaryById failed:', err);
    return null;
  }
}

export async function getShiftSummaryByDateAndShift(
  date: string,
  shift: 'NIGHT' | 'DAY' | 'EVENING',
): Promise<ShiftSummaryRecord | null> {
  const data = await graphqlRequest(LIST_BY_DATE_AND_SHIFT, {
    date,
    shift: { eq: shift },
  });
  const items: ShiftSummaryRecord[] = data.listShiftSummaryByDateAndShift?.items ?? [];
  return items.length > 0 ? items[0] : null;
}

/**
 * Subscribe to updates on a specific shift summary record.
 * Uses polling as a simple cross-browser approach (AppSync WebSocket subscriptions
 * require additional Amplify client setup). Polls every 10 seconds.
 */
export function subscribeToShiftUpdates(
  recordId: string,
  onUpdate: (record: ShiftSummaryRecord) => void,
): { unsubscribe: () => void } {
  let active = true;
  let lastUpdatedAt = '';

  const poll = async () => {
    while (active) {
      try {
        const data = await graphqlRequest(GET_SHIFT_SUMMARY, { id: recordId });
        const record = data.getShiftSummary as ShiftSummaryRecord | null;
        if (record && record.updatedAt && record.updatedAt !== lastUpdatedAt) {
          lastUpdatedAt = record.updatedAt;
          onUpdate(record);
        }
      } catch {
        // Silently retry on next poll
      }
      await new Promise((r) => setTimeout(r, 10_000));
    }
  };

  poll();

  return {
    unsubscribe: () => { active = false; },
  };
}

export async function listShiftSummaries(limit = 100): Promise<ShiftSummaryRecord[]> {
  const data = await graphqlRequest(LIST_SHIFT_SUMMARIES, { limit });
  const items: ShiftSummaryRecord[] = data.listShiftSummaries.items;

  return items.sort((a, b) => {
    const dateCompare = (b.date ?? '').localeCompare(a.date ?? '');
    if (dateCompare !== 0) return dateCompare;
    const shiftOrder: Record<string, number> = { EVENING: 0, DAY: 1, NIGHT: 2 };
    return (shiftOrder[a.shift] ?? 0) - (shiftOrder[b.shift] ?? 0);
  });
}
